import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { CustomsCalculation, CustomsCalculationsService } from '../../../services/api/customs-calculations';
import { AuthService } from '../../../services/auth/auth';
import { LanguageService } from '../../../services/i18n/language';
import { UpgradeToCargoOwnerModalComponent } from '../upgrade-to-cargo-owner-modal/upgrade-to-cargo-owner-modal';

const WHATSAPP_URL = 'https://wa.me/77078880054';

/**
 * Заявка на платное сопровождение поставки Bilload.
 * «Подтвердить заявку» — прежняя кнопка «Обработать»: ведёт на страницу обработки расчёта
 * (вкладки «Расчёт», «Поставка», «Перевозка»), которая сама переводит его в работу (CREATED → IN_PROCESSING).
 */
@Component({
  selector: 'app-order-support',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, UpgradeToCargoOwnerModalComponent],
  templateUrl: './order-support.html',
  styleUrl: './order-support.scss'
})
export class OrderSupportComponent implements OnInit, OnDestroy {
  calculation: CustomsCalculation | null = null;
  loading = true;
  error: string | null = null;
  agreed = false;
  /** Клиент по телефону: перед заявкой заполняет данные компании — окно прямо на этой странице. */
  showUpgrade = false;

  /** Новый пользователь по телефону — после подтверждения ему предложат открыть полный доступ. */
  isPublicUser = false;
  private numberLocale = 'ru-RU';
  private subscriptions = new Subscription();

  /** Что входит в сопровождение. */
  readonly services = [
    { key: 'orderSupport.svcAccess', fallback: 'Полный доступ к платформе Bilload' },
    { key: 'orderSupport.svcBids', fallback: 'Сбор ставок на перевозку от экспедиторов и их сравнение' },
    { key: 'orderSupport.svcDeals', fallback: 'Ведение сделок по перевозке на платформе' },
    { key: 'orderSupport.svcPermits', fallback: 'Заказ оформления разрешительных документов' },
    { key: 'orderSupport.svcDocsControl', fallback: 'Проверка документов со стороны Bilload' },
    { key: 'orderSupport.svcCustoms', fallback: 'Таможенное оформление грузов силами Bilload' },
    { key: 'orderSupport.svcManager', fallback: 'Персональный менеджер по вашей поставке' },
    { key: 'orderSupport.svcStatus', fallback: 'Уведомления о статусе поставки на каждом этапе' },
    { key: 'orderSupport.svcSupport', fallback: 'Поддержка 24/7' },
    { key: 'orderSupport.svcArchive', fallback: 'Все документы в цифровом архиве' }
  ];

  /**
   * Номер заявки: BL-<год>-<номер расчёта>. Отдельной заявки на сервере пока нет (см. ТЗ, пункт 3),
   * поэтому номер строим от расчёта — он постоянный и совпадёт с тем, что увидит менеджер.
   */
  get requestNumber(): string {
    const c = this.calculation;
    if (!c) return '';
    const year = c.createdAt ? new Date(c.createdAt).getFullYear() : new Date().getFullYear();
    return `BL-${year}-${String(c.id).padStart(3, '0')}`;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private calculations: CustomsCalculationsService,
    private auth: AuthService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  /** Стоимость товаров по инвойсу в его валюте; разные валюты — через «+». */
  get invoiceValue(): string {
    const sums = new Map<string, number>();
    for (const p of this.calculation?.products ?? []) {
      if (p.productCost == null) continue;
      const cur = p.currencyCode || 'USD';
      sums.set(cur, (sums.get(cur) ?? 0) + Number(p.productCost));
    }
    if (!sums.size) return '—';
    const fmt = new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 2 });
    return [...sums.entries()].map(([cur, sum]) => fmt.format(sum) + ' ' + cur).join(' + ');
  }

  /** WhatsApp менеджеру: в сообщении сразу номер заявки и расчёта. */
  get whatsappLink(): string {
    const text = this.t('orderSupport.helpMessage', 'Здравствуйте! Вопрос по заявке {request} (расчёт № {id})')
      .replace('{request}', this.requestNumber)
      .replace('{id}', String(this.calculation?.id ?? ''));
    return `${WHATSAPP_URL}?text=${encodeURIComponent(text)}`;
  }

  ngOnInit(): void {
    if (typeof window === 'undefined') return;
    if (!this.auth.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }
    const user = this.auth.getUser();
    this.isPublicUser = user?.role?.code === 'ROLE_PUBLIC';

    this.subscriptions.add(this.languageService.getCurrentLanguage().subscribe(lang => {
      this.numberLocale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
      this.cdr.markForCheck();
    }));
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));

    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.subscriptions.add(
      this.calculations.getCalculationById(id).subscribe({
        next: calc => {
          // Заявка уже подтверждена — сразу на страницу обработки
          if (calc.status && calc.status !== 'CREATED') {
            this.router.navigate(['/calculations', calc.id, 'process'], { replaceUrl: true });
            return;
          }
          this.calculation = calc;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = this.t('orderSupport.loadError', 'Не удалось загрузить расчёт. Обновите страницу.');
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  fmt(value: number | null | undefined): string {
    return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 0 }).format(Number(value ?? 0));
  }

  onUpgraded(): void {
    this.showUpgrade = false;
    this.isPublicUser = false;
    if (this.calculation) {
      this.router.navigate(['/calculations', this.calculation.id, 'process']);
    }
  }

  // ─── Подтверждение ──────────────────────────────────────────────────────────

  confirm(): void {
    if (!this.calculation || !this.agreed) return;
    // Новый клиент (в том числе гость без телефона) — сразу данные компании: ФИО и телефон есть в этой же форме.
    // После неё он грузовладелец, и обработка начнётся сама
    if (this.isPublicUser || this.auth.isGuest()) {
      this.showUpgrade = true;
      return;
    }
    this.router.navigate(['/calculations', this.calculation.id, 'process']);
  }
}
